import React, { useEffect, useRef, useState } from 'react';
import { FaGavel, FaSearchDollar, FaTruck } from 'react-icons/fa';

function ServicesSection() {
    const [isVisible, setIsVisible] = useState(false);
    const sectionRef = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                }
            },
            { threshold: 0.2 }
        );

        if (sectionRef.current) {
            observer.observe(sectionRef.current);
        }

        return () => observer.disconnect();
    }, []);

    const services = [
        {
            icon: <FaGavel />,
            title: "Évaluation d'Antiquités",
            description: "Nous mettons à votre disposition des services professionnels d'évaluation, réalisés avec soin et expertise, pour déterminer la valeur de vos pièces antiques et vous offrir une estimation fiable et précise.",
            color: "#d4b875"
        },
        {
            icon: <FaSearchDollar />,
            title: "Estimation Gratuite",
            description: "Nous vous offrons un service complet comprenant le déplacement et une expertise entièrement gratuits afin d'évaluer la valeur de vos objets en toute précision et transparence.",
            color: "#e6c889"
        },
        {
            icon: <FaTruck />,
            title: "Service à Domicile",
            description: "Dès que notre offre est acceptée, nous fixons ensemble un rendez-vous pour collecter gratuitement les objets directement chez vous. Vous n'avez rien à gérez, nous nous occupons de tout.",
            color: "#f0d49d"
        },
    ];

    return (
        <section className="services-section" id="services" ref={sectionRef}>
            <div className="background-pattern"></div>
            <div className="section-container">
                <header className="section-header">
                    <div className="title-wrapper">
                        <h2 className={isVisible ? 'animate-title' : ''}>Nos Services</h2>
                        <div className="title-underline"></div>
                    </div>
                    <p className="section-subtitle">
                        Une expertise professionnelle à votre service
                    </p>
                </header>

                <div className="services-container">
                    {services.map((service, index) => (
                        <div 
                            key={index} 
                            className={`service-card ${isVisible ? 'animate-card' : ''}`}
                            style={{ 
                                '--delay': `${index * 0.2}s`,
                                '--accent-color': service.color
                            }}
                        >
                            <div className="card-glow"></div>
                            <div className="icon-container">
                                <div className="icon-background"></div>
                                <div className="service-icon">{service.icon}</div>
                            </div>
                            <div className="card-content">
                                <h3>{service.title}</h3>
                                <p>{service.description}</p>
                            </div>
                            <div className="card-border"></div>
                        </div>
                    ))}
                </div>
            </div>

            <style jsx>{`
                .services-section {
                    padding: 100px 24px;
                    background: linear-gradient(135deg, #1a3a33 0%, #20453d 50%, #1a3a33 100%);
                    position: relative;
                    overflow: hidden;
                }

                .background-pattern {
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background-image: 
                        radial-gradient(circle at 25% 25%, rgba(192, 162, 107, 0.1) 0%, transparent 25%),
                        radial-gradient(circle at 75% 75%, rgba(192, 162, 107, 0.05) 0%, transparent 25%);
                    animation: patternFloat 20s ease-in-out infinite;
                }

                @keyframes patternFloat {
                    0%, 100% { transform: translateY(0px) rotate(0deg); }
                    50% { transform: translateY(-20px) rotate(2deg); }
                }

                .section-container {
                    max-width: 1200px;
                    margin: 0 auto;
                    position: relative;
                    z-index: 2;
                }

                .section-header {
                    text-align: center;
                    margin-bottom: 80px;
                }

                .title-wrapper {
                    position: relative;
                    display: inline-block;
                    margin-bottom: 24px;
                }

                .section-header h2 {
                    font-size: 3.5rem;
                    font-weight: 700;
                    color: #C0A26B;
                    margin: 0;
                    letter-spacing: -0.02em;
                    text-transform: uppercase;
                    opacity: 0;
                    transform: translateY(30px);
                    transition: all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
                }

                .animate-title {
                    opacity: 1 !important;
                    transform: translateY(0) !important;
                }

                .title-underline {
                    position: absolute;
                    bottom: -8px;
                    left: 50%;
                    transform: translateX(-50%);
                    width: 0;
                    height: 4px;
                    background: linear-gradient(90deg, transparent, #C0A26B, transparent);
                    transition: width 1s ease 0.5s;
                }

                .animate-title + .title-underline {
                    width: 120%;
                }

                .section-subtitle {
                    font-size: 1.3rem;
                    color: rgba(192, 162, 107, 0.8);
                    margin: 0;
                    font-style: italic;
                }

                .services-container {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
                    gap: 40px;
                    justify-items: center;
                }

                .service-card {
                    background: linear-gradient(145deg, #fff 0%, #f8fafb 100%);
                    border-radius: 24px;
                    padding: 40px 32px;
                    position: relative;
                    width: 100%;
                    max-width: 380px;
                    opacity: 0;
                    transform: translateY(60px) rotateX(15deg);
                    transition: all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
                    cursor: pointer;
                    overflow: hidden;
                }

                .animate-card {
                    opacity: 1 !important;
                    transform: translateY(0) rotateX(0) !important;
                    transition-delay: var(--delay);
                }

                .card-glow {
                    position: absolute;
                    top: -50%;
                    left: -50%;
                    width: 200%;
                    height: 200%;
                    background: radial-gradient(circle, var(--accent-color) 0%, transparent 70%);
                    opacity: 0;
                    transition: opacity 0.5s ease;
                    z-index: 0;
                }

                .service-card:hover .card-glow {
                    opacity: 0.1;
                }

                .service-card:hover {
                    transform: translateY(-12px) scale(1.02);
                    box-shadow: 
                        0 25px 50px rgba(0,0,0,0.2),
                        0 0 0 1px rgba(192, 162, 107, 0.2),
                        inset 0 1px 0 rgba(255,255,255,0.5);
                }

                .card-border {
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    border-radius: 24px;
                    padding: 2px;
                    background: linear-gradient(135deg, var(--accent-color), transparent, var(--accent-color));
                    opacity: 0;
                    transition: opacity 0.3s ease;
                }

                .service-card:hover .card-border {
                    opacity: 0.6;
                }

                .card-border::before {
                    content: '';
                    position: absolute;
                    top: 2px;
                    left: 2px;
                    right: 2px;
                    bottom: 2px;
                    background: linear-gradient(145deg, #fff 0%, #f8fafb 100%);
                    border-radius: 22px;
                }

                .icon-container {
                    position: relative;
                    width: 80px;
                    height: 80px;
                    margin: 0 auto 32px;
                    z-index: 2;
                }

                .icon-background {
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    background: linear-gradient(135deg, var(--accent-color) 0%, #C0A26B 100%);
                    border-radius: 50%;
                    transform: scale(0);
                    transition: transform 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55);
                    box-shadow: 0 8px 32px rgba(192, 162, 107, 0.3);
                }

                .animate-card .icon-background {
                    transform: scale(1);
                    transition-delay: calc(var(--delay) + 0.3s);
                }

                .service-icon {
                    position: relative;
                    z-index: 2;
                    font-size: 2.2rem;
                    color: #20453d;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 100%;
                    height: 100%;
                    transform: scale(0) rotate(-180deg);
                    transition: transform 0.8s cubic-bezier(0.68, -0.55, 0.265, 1.55);
                }

                .animate-card .service-icon {
                    transform: scale(1) rotate(0deg);
                    transition-delay: calc(var(--delay) + 0.5s);
                }

                .service-card:hover .service-icon {
                    transform: scale(1.1) rotate(5deg);
                }

                .card-content {
                    position: relative;
                    z-index: 2;
                }

                .service-card h3 {
                    font-size: 1.5rem;
                    font-weight: 700;
                    color: #1f2b2a;
                    margin: 0 0 16px;
                    opacity: 0;
                    transform: translateY(20px);
                    transition: all 0.6s ease;
                }

                .animate-card h3 {
                    opacity: 1;
                    transform: translateY(0);
                    transition-delay: calc(var(--delay) + 0.7s);
                }

                .service-card p {
                    font-size: 1.05rem;
                    line-height: 1.7;
                    color: #44504f;
                    margin: 0;
                    opacity: 0;
                    transform: translateY(20px);
                    transition: all 0.6s ease;
                }

                .animate-card p {
                    opacity: 1;
                    transform: translateY(0);
                    transition-delay: calc(var(--delay) + 0.9s);
                }

                .service-card::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    height: 4px;
                    background: linear-gradient(90deg, var(--accent-color), #C0A26B, var(--accent-color));
                    border-radius: 24px 24px 0 0;
                    transform: scaleX(0);
                    transition: transform 0.6s ease;
                }

                .animate-card::before {
                    transform: scaleX(1);
                    transition-delay: calc(var(--delay) + 1.1s);
                }

                @media (max-width: 1024px) {
                    .services-container {
                        grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
                        gap: 32px;
                    }

                    .section-header h2 {
                        font-size: 3rem;
                    }
                }

                @media (max-width: 768px) {
                    .services-section {
                        padding: 80px 20px;
                    }

                    .section-header {
                        margin-bottom: 60px;
                    }

                    .section-header h2 {
                        font-size: 2.5rem;
                    }

                    .section-subtitle {
                        font-size: 1.1rem;
                    }

                    .services-container {
                        grid-template-columns: 1fr;
                        gap: 24px;
                    }

                    .service-card {
                        padding: 32px 24px;
                        max-width: none;
                    }

                    .icon-container {
                        width: 70px;
                        height: 70px;
                        margin-bottom: 24px;
                    }

                    .service-icon {
                        font-size: 1.8rem;
                    }

                    .service-card h3 {
                        font-size: 1.3rem;
                    }

                    .service-card p {
                        font-size: 1rem;
                    }
                }
            `}</style>
        </section>
    );
}

export default ServicesSection;
